import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * On-device voice search. Captures the mic, transcribes locally with a
 * Whisper model running in a Web Worker (transformers.js / WASM), and hands
 * back the final transcript via `onResult`. No API key, no server — works in
 * any WASM-capable browser (Brave/Firefox/Chrome).
 *
 * First use downloads the model (~40MB, then cached): the user can start
 * speaking immediately; transcription waits for the model if it isn't ready.
 * Plays /sfx/voice-start.wav on start and /sfx/voice-end.wav on stop.
 */

export type VoiceState =
  | 'idle'
  | 'loading'
  | 'listening'
  | 'transcribing'
  | 'error';

export interface UseVoiceSearch {
  supported: boolean;
  state: VoiceState;
  progress: number;
  level: number;
  heard: boolean;
  error: string | null;
  start: () => void;
  stop: () => void;
}

const SILENCE_RMS = 0.012;
const SILENCE_STOP_MS = 1300;
const MAX_LISTEN_MS = 14000;

function detectSupport(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as unknown as { webkitAudioContext?: unknown };
  const hasAudioCtx =
    typeof window.AudioContext !== 'undefined' ||
    typeof w.webkitAudioContext !== 'undefined';
  return (
    typeof Worker !== 'undefined' &&
    typeof OfflineAudioContext !== 'undefined' &&
    hasAudioCtx &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

function getAudioCtxCtor(): typeof AudioContext {
  const w = window as unknown as { webkitAudioContext?: typeof AudioContext };
  return window.AudioContext ?? w.webkitAudioContext;
}

function rms(samples: Float32Array): number {
  const sum = samples.reduce((acc, v) => acc + v * v, 0);
  return Math.sqrt(sum / Math.max(1, samples.length));
}

async function resampleMono16k(
  samples: Float32Array,
  srcRate: number
): Promise<Float32Array> {
  if (srcRate === 16000 || samples.length === 0) return samples;
  const length = Math.max(1, Math.ceil((samples.length * 16000) / srcRate));
  const offline = new OfflineAudioContext(1, length, 16000);
  const buffer = offline.createBuffer(1, samples.length, srcRate);
  buffer.copyToChannel(new Float32Array(samples), 0);
  const source = offline.createBufferSource();
  source.buffer = buffer;
  source.connect(offline.destination);
  source.start(0);
  const rendered = await offline.startRendering();
  return rendered.getChannelData(0).slice();
}

function concatFloat32(chunks: Float32Array[]): Float32Array {
  const total = chunks.reduce((acc, c) => acc + c.length, 0);
  const out = new Float32Array(total);
  chunks.reduce((offset, c) => {
    out.set(c, offset);
    return offset + c.length;
  }, 0);
  return out;
}

interface Capture {
  ctx: AudioContext;
  stream: MediaStream;
  processor: ScriptProcessorNode;
  source: MediaStreamAudioSourceNode;
  sink: GainNode;
  chunks: Float32Array[];
}

export function useVoiceSearch(params: {
  onResult: (transcript: string) => void;
}): UseVoiceSearch {
  const [supported] = useState(detectSupport);
  const [state, setState] = useState<VoiceState>('idle');
  const [progress, setProgress] = useState(0);
  const [level, setLevel] = useState(0);
  const [heard, setHeard] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onResultRef = useRef(params.onResult);
  useEffect(() => {
    onResultRef.current = params.onResult;
  });

  const workerRef = useRef<Worker | null>(null);
  const modelReadyRef = useRef(false);
  const captureRef = useRef<Capture | null>(null);
  const startAudioRef = useRef<HTMLAudioElement | null>(null);
  const endAudioRef = useRef<HTMLAudioElement | null>(null);
  const pendingAudioRef = useRef<Float32Array | null>(null);
  const silenceMsRef = useRef(0);
  const elapsedMsRef = useRef(0);
  const heardSpeechRef = useRef(false);

  const playSound = useCallback((ref: typeof startAudioRef): void => {
    const el = ref.current;
    if (!el) return;
    try {
      el.currentTime = 0;
      void el.play();
    } catch {
      /* autoplay/permission — ignore */
    }
  }, []);

  const submitAudio = useCallback((audio: Float32Array): void => {
    if (audio.length === 0) {
      setState('idle');
      return;
    }
    setState('transcribing');
    if (modelReadyRef.current) {
      workerRef.current?.postMessage({ kind: 'transcribe', audio });
    } else {
      pendingAudioRef.current = audio;
      workerRef.current?.postMessage({ kind: 'load' });
    }
  }, []);

  const teardownCapture = useCallback((): Capture | null => {
    const cap = captureRef.current;
    captureRef.current = null;
    if (!cap) return null;
    try {
      cap.processor.onaudioprocess = null;
      cap.source.disconnect();
      cap.processor.disconnect();
      cap.sink.disconnect();
      cap.stream.getTracks().forEach((t) => t.stop());
      void cap.ctx.close();
    } catch {
      /* noop */
    }
    return cap;
  }, []);

  const stop = useCallback((): void => {
    const cap = teardownCapture();
    setLevel(0);
    if (!cap) {
      if (state === 'listening') setState('idle');
      return;
    }
    playSound(endAudioRef);
    const merged = concatFloat32(cap.chunks);
    void resampleMono16k(merged, cap.ctx.sampleRate)
      .then((audio) => submitAudio(audio))
      .catch(() => {
        setError('Could not process audio');
        setState('error');
      });
  }, [playSound, state, submitAudio, teardownCapture]);

  const stopRef = useRef(stop);
  useEffect(() => {
    stopRef.current = stop;
  });

  const onFrame = useCallback((data: Float32Array, srcRate: number): void => {
    const cap = captureRef.current;
    if (!cap) return;
    cap.chunks.push(new Float32Array(data));
    const frameMs = (data.length / srcRate) * 1000;
    elapsedMsRef.current += frameMs;
    const loudness = rms(data);
    setLevel(Math.min(1, loudness * 8));
    if (loudness >= SILENCE_RMS) {
      if (!heardSpeechRef.current) setHeard(true);
      heardSpeechRef.current = true;
      silenceMsRef.current = 0;
    } else {
      silenceMsRef.current += frameMs;
    }
    const silenceStop =
      heardSpeechRef.current && silenceMsRef.current >= SILENCE_STOP_MS;
    if (silenceStop || elapsedMsRef.current >= MAX_LISTEN_MS) {
      stopRef.current();
    }
  }, []);

  const beginCapture = useCallback(
    (stream: MediaStream): void => {
      const AudioCtor = getAudioCtxCtor();
      const ctx = new AudioCtor();
      void ctx.resume();
      const source = ctx.createMediaStreamSource(stream);
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      const sink = ctx.createGain();
      sink.gain.value = 0;
      const cap: Capture = { ctx, stream, processor, source, sink, chunks: [] };
      captureRef.current = cap;
      silenceMsRef.current = 0;
      elapsedMsRef.current = 0;
      heardSpeechRef.current = false;
      processor.onaudioprocess = (e: AudioProcessingEvent): void => {
        onFrame(e.inputBuffer.getChannelData(0), ctx.sampleRate);
      };
      source.connect(processor);
      processor.connect(sink);
      sink.connect(ctx.destination);
      setState('listening');
    },
    [onFrame]
  );

  const ensureWorker = useCallback((): Worker => {
    if (workerRef.current) return workerRef.current;
    const worker = new Worker(
      new URL('../lib/voice/whisper-worker.ts', import.meta.url),
      { type: 'module' }
    );
    worker.onmessage = (event: MessageEvent): void => {
      const msg = event.data as {
        kind: string;
        payload?: { status?: string; progress?: number };
        text?: string;
        message?: string;
      };
      if (msg.kind === 'progress' && msg.payload?.status === 'progress') {
        setProgress(Math.round(msg.payload.progress ?? 0));
      } else if (msg.kind === 'ready') {
        modelReadyRef.current = true;
        setProgress(100);
        const pending = pendingAudioRef.current;
        pendingAudioRef.current = null;
        if (pending) workerRef.current?.postMessage({ kind: 'transcribe', audio: pending });
      } else if (msg.kind === 'result') {
        const text = (msg.text ?? '').trim();
        if (text) onResultRef.current(text);
        setState('idle');
      } else if (msg.kind === 'error') {
        setError(msg.message ?? 'Voice model failed');
        setState('error');
      }
    };
    workerRef.current = worker;
    return worker;
  }, []);

  const start = useCallback((): void => {
    if (!supported || state === 'listening' || state === 'transcribing') return;
    setError(null);
    setHeard(false);
    playSound(startAudioRef);
    const worker = ensureWorker();
    if (!modelReadyRef.current) {
      setProgress(0);
      worker.postMessage({ kind: 'load' });
    }
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => beginCapture(stream))
      .catch(() => {
        setError('Microphone access denied');
        setState('error');
      });
  }, [beginCapture, ensureWorker, playSound, state, supported]);

  useEffect(() => {
    if (!supported) return undefined;
    startAudioRef.current = new Audio('/sfx/voice-start.wav');
    endAudioRef.current = new Audio('/sfx/voice-end.wav');
    startAudioRef.current.preload = 'auto';
    endAudioRef.current.preload = 'auto';
    return () => {
      teardownCapture();
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, [supported, teardownCapture]);

  return { supported, state, progress, level, heard, error, start, stop };
}

export default useVoiceSearch;
